import { lazy, type ComponentType } from "react";

// Self-healing React.lazy for route chunks.
//
// A deploy replaces every content-hashed chunk. A tab that stayed open across
// the deploy lazy-imports chunks that no longer exist: the fetch 404s, or —
// worse, seen live on staging Safari — the browser's module registry hands
// back a corrupted/EMPTY namespace for the failed chunk, so the import
// RESOLVES with `default: undefined` and React throws
// `undefined is not an object (evaluating 'e._result.default')` into the
// route error boundary.
//
// Remedy (industry-standard): on a failed OR default-less chunk, force ONE
// reload. index.html is served no-cache (docker/nginx.conf), so the reload
// moves the tab onto the new deploy's consistent chunk graph. A
// sessionStorage guard prevents a reload loop when the failure is a real
// build bug — the second failure rethrows into the error boundary as before.

const RELOAD_GUARD_KEY = "airplate.lazy-chunk-reloaded";

function getGuard(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    return true; // storage unavailable (private mode) — never auto-reload
  }
}

function setGuard(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    else sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore — guard degrades to "no auto-reload"
  }
}

// `any` (not `unknown`): props are contravariant, so ComponentType<unknown>
// rejects every component with concrete props. React's own lazy() typing uses
// the same ComponentType<any> constraint — this mirrors it exactly.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  /** Route/page name baked into the failure message — a default-less chunk
   *  has been seen live only on one machine, so the error must identify
   *  WHICH module failed and what shape it resolved with. */
  name = "unknown",
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      const mod = await importer();
      if (!mod?.default) {
        // Include the resolved namespace's keys: an EMPTY list points at a
        // blocked/corrupted fetch, a WRONG key set points at a chunk-graph
        // (bundler) bug mapping the specifier to the wrong module.
        const shape = mod ? Object.keys(mod).slice(0, 10).join(",") : String(mod);
        throw new Error(
          `lazy chunk "${name}" resolved without a default export (keys: [${shape}])`,
        );
      }
      // A clean load clears the guard so a FUTURE deploy skew can heal too.
      setGuard(false);
      return mod;
    } catch (err) {
      if (!getGuard()) {
        setGuard(true);
        window.location.reload();
        // The reload tears the page down — park the lazy forever so React
        // doesn't flash the error boundary during the teardown.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
